import React, { useState, useEffect } from 'react';
import { Row, Col, Tag, message, notification } from 'antd';
import { updateExportStatus, showExportExtension } from '@app/api/export-extention.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';
import RejectPopup from '../../common/RejectPopup';

interface ExportWorkflowButtonsProps {
  status: string | null;
  id: string | number | null;
  export_id: string | number | null;
  setStatus: React.Dispatch<React.SetStateAction<string | null>>;
  wfActions: string[];
  currentType: 'NORMAL';
}

const EXP_EXTENSION_ACTIONS: Record<string, string> = {
  DRAFT_TO_PENDING_REQUEST_LETTER: 'Create Task',
  PENDING_REQUEST_LETTER_TO_PENDING_APPROVAL: 'Submit Request Letter',
  PENDING_APPROVAL_TO_PENDING_REQUEST_LETTER: 'Resubmit Request Letter',
  PENDING_APPROVAL_TO_PENDING_CERTIFICATE: 'Proceed to Pending Certificate',
  PENDING_APPROVAL_TO_COMPLETED: 'Mark as Complete',
  PENDING_APPROVAL_TO_REJECTED: 'Reject',
  REJECTED_TO_PENDING_APPROVAL: 'Resubmit for Approval',
  PENDING_CERTIFICATE_TO_COMPLETED: 'Complete',
};

const PopupActions = ['PENDING_APPROVAL_TO_REJECTED', 'PENDING_APPROVAL_TO_PENDING_REQUEST_LETTER'];

const ExportWorkflowButtons: React.FC<ExportWorkflowButtonsProps> = ({
  status,
  id,
  export_id,
  setStatus,
  wfActions,
  currentType,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localWfActions, setLocalWfActions] = useState<string[]>(wfActions || []);
  const [isRejectModalVisible, setRejectModalVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [popupTitle, setPopupTitle] = useState('Reject');
  const handleErrorMessages = useApiErrorHandler();

  // Keep localWfActions in sync if wfActions prop changes
  useEffect(() => {
    setLocalWfActions(wfActions || []);
  }, [wfActions]);

  if (!id) return null;

  const actionLabels = EXP_EXTENSION_ACTIONS;

  const handleWorkflowAction = async (action: string) => {
    if (action === 'PENDING_VERIFICATION_AQO_TO_PENDING_CUSTOMER_VISIT') {
      const isDatesSet = true; // Replace with real logic if needed
      if (!isDatesSet) {
        notification.error({
          message: 'Cannot Proceed',
          description: 'Please set required dates before continuing.',
        });
        return;
      }
    }

    await handleStatusChange(action);
  };

  const handleStatusChange = async (
    action: string,
    extraData?: {
      comment_for?: string;
      msg?: string;
    },
  ) => {
    try {
      await updateExportStatus(Number(id), {
        action,
        ...extraData,
      });
      const exportData = await showExportExtension(Number(id));
      const newStatus = exportData?.export_extension?.status;
      const newWfActions = exportData?.wf_actions || [];

      if (newStatus) {
        setStatus(newStatus);
        setLocalWfActions(newWfActions);
        message.success(`Export status updated to ${newStatus}.`);
      } else {
        message.warning('Export status updated but response did not include status.');
      }
    } catch (err: any) {
      handleErrorMessages(err);
      console.error('Error updating export status:', err);
    }
  };

  const handleRejectSubmit = async (values: { commentFor: string; comment: string }) => {
    if (!pendingAction) return;

    setIsSubmitting(true);

    try {
      await handleStatusChange(pendingAction, {
        comment_for: values.commentFor,
        msg: values.comment,
      });

      setRejectModalVisible(false);
      setPendingAction(null);
    } catch (error) {
      console.error('Error submitting rejection:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Row
      justify="space-between"
      align="middle"
      style={{
        padding: '12px 16px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
        minHeight: '64px',
      }}
    >
      <Col flex="auto">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          {status !== 'COMPLETE' && status !== 'DONE' && status !== 'REJECTED' && localWfActions.length > 0
            ? localWfActions.map((action) => {
                const label = actionLabels[action];
                if (!label) return null;

                return (
                  <BaseButton
                    key={action}
                    type="primary"
                    size="small"
                    disabled={isSubmitting}
                    onClick={() => {
                      if (PopupActions.includes(action)) {
                        setPendingAction(action);
                        setPopupTitle(actionLabels[action] || 'Action');
                        setRejectModalVisible(true);
                        return;
                      }

                      handleWorkflowAction(action);
                    }}
                  >
                    {label}
                  </BaseButton>
                );
              })
            : null}
        </div>

        <RejectPopup
          key={pendingAction || 'default'}
          title={popupTitle}
          visible={isRejectModalVisible}
          onClose={() => {
            setRejectModalVisible(false);
            setPendingAction(null);
          }}
          onSubmit={handleRejectSubmit}
          isLoading={isSubmitting}
        />
      </Col>
      <Col>
        <span style={{ fontWeight: 'bold', marginRight: 5 }}>Status:</span>
        <Tag
          color={status === 'COMPLETE' || status === 'DONE' ? 'green' : status === 'REJECTED' ? 'red' : 'orange'}
          style={{ fontSize: 14, padding: '4px 10px', borderRadius: 6 }}
        >
          {status || 'N/A'}
        </Tag>
      </Col>
    </Row>
  );
};

export default ExportWorkflowButtons;
